import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { calendarApi, projectsApi, tasksApi } from "@/lib/api";
import ContactPicker from "@/components/ContactPicker";
import MultiContactPicker from "@/components/MultiContactPicker";
import { toast } from "sonner";
import { ExternalLink, Info } from "lucide-react";

const empty = {
  title: "", description: "", event_type: "meeting",
  starts_at: "", ends_at: "", location: "",
  project_id: "", contact_id: "", participant_contact_ids: [],
};

// A derived calendar row is a projection of a canonical source record
// (Project.target_completion or Task.due_date). The Calendar API returns
// them alongside real events with `derived: true` and `source_type` /
// `source_id` so the client can route edits back to the source. These
// records are date-only — never synthesize or display a time.
const isDerived = (ev) => !!ev && (ev.derived === true || !!ev.source_type);

export default function EventDialog({ open, setOpen, event, entityId, projects, contacts, onSaved }) {
  const [form, setForm] = useState(empty);
  const [dateOnly, setDateOnly] = useState("");   // separate YYYY-MM-DD state for derived rows
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const derived = useMemo(() => isDerived(event), [event]);
  const sourceType = event?.source_type;   // "project" | "task"
  const sourceId = event?.source_id;

  useEffect(() => {
    if (event) {
      // For derived rows, we only care about the date part of starts_at
      // (backend synthesizes 09:00 or 17:00 UTC purely for sort order —
      // the source record is date-only). The dialog must never surface
      // that synthetic time to the user.
      const dateStr = (event.starts_at || "").slice(0, 10);
      setDateOnly(dateStr);
      setForm({
        ...empty,
        ...event,
        starts_at: event.starts_at?.slice(0, 16) || "",
        ends_at: event.ends_at?.slice(0, 16) || "",
      });
    } else {
      setDateOnly("");
      setForm(empty);
    }
  }, [event, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openSource = () => {
    if (!event?.entity_id || !sourceType || !sourceId) return;
    const tab = sourceType === "project" ? "projects" : "tasks";
    setOpen(false);
    navigate(`/entity/${event.entity_id}?tab=${tab}&focus=${sourceId}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (derived) {
        if (!dateOnly) return toast.error("Date is required");
        // Route to the canonical source record. Only the date is safely
        // editable from Calendar — every other visible field (title,
        // description, participants) is a projection of the source and
        // is managed on the source itself.
        if (sourceType === "project" && sourceId) {
          await projectsApi.update(sourceId, { target_completion: dateOnly });
          toast.success("Project target completion updated");
        } else if (sourceType === "task" && sourceId) {
          await tasksApi.update(sourceId, { due_date: dateOnly });
          toast.success("Task due date updated");
        } else {
          toast.error("This item is generated — open its source to edit.");
          return;
        }
      } else {
        if (!form.title.trim() || !form.starts_at) return toast.error("Title and start time required");
        const payload = {
          ...form,
          entity_id: entityId,
          starts_at: new Date(form.starts_at).toISOString(),
        };
        payload.ends_at = form.ends_at ? new Date(form.ends_at).toISOString() : null;
        if (!payload.project_id) payload.project_id = null;
        if (!payload.contact_id) payload.contact_id = null;
        if (event?.id) await calendarApi.update(event.id, payload);
        else await calendarApi.create(payload);
        toast.success("Saved");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Derived view: minimal, focused. Show ONLY title (readonly, contextual)
  // + banner + date-only picker. Every other field on a derived record is
  // either empty, computed, or lives on the source — hiding them removes
  // the visual noise that made the earlier save-doesn't-persist bug so
  // easy to miss.
  if (derived) {
    const isProject = sourceType === "project";
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-[#151A23] border-white/10 text-white w-[calc(100vw-2rem)] max-w-md sm:max-w-lg"
          data-testid="event-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display" data-testid="event-dialog-title">
              {isProject ? "Edit project milestone" : "Edit task deadline"}
            </DialogTitle>
          </DialogHeader>

          <div
            data-testid="derived-event-banner"
            className="rounded-md border border-amber-400/25 bg-amber-400/10 text-amber-100 text-xs px-3 py-2 flex flex-col sm:flex-row sm:items-start gap-2"
          >
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div className="leading-relaxed">
                {isProject
                  ? "This milestone is generated from the Project's target completion date. Changing the date here updates the Project. Title, description, and participants are managed on the Project."
                  : "This deadline is generated from the Task's due date. Changing the date here updates the Task. Title, assignee, and description are managed on the Task."}
              </div>
            </div>
            <button
              type="button"
              onClick={openSource}
              data-testid="derived-open-source-btn"
              className="text-amber-200 hover:text-amber-50 inline-flex items-center gap-1 self-start sm:self-auto font-medium whitespace-nowrap"
            >
              {isProject ? "Open Project" : "Open Task"}
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>

          <div className="py-2 space-y-4">
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">
                {isProject ? "Milestone" : "Deadline"}
              </Label>
              <Input
                data-testid="derived-title-readonly"
                value={form.title || ""}
                readOnly
                className="mt-1.5 bg-white/5 border-white/10 text-white/80 cursor-default"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs uppercase tracking-wider">
                {isProject ? "Target completion" : "Due date"}
              </Label>
              <Input
                type="date"
                data-testid="derived-date-input"
                value={dateOnly}
                onChange={(e) => setDateOnly(e.target.value)}
                className="mt-1.5 bg-white/5 border-white/10 w-full"
              />
              <div className="text-[11px] text-white/40 mt-1.5">
                Date only. Saving updates the {isProject ? "Project" : "Task"} record.
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white hover:bg-white/10 w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !dateOnly}
              data-testid="event-save-btn"
              className="rounded-full bg-white text-black hover:bg-white/90 w-full sm:w-auto"
            >
              {saving ? "Saving…" : "Save date"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Manual event: full editor (unchanged).
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-lg" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="event-dialog-title">
            {event?.id ? "Edit event" : "New event"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Title</Label>
            <Input data-testid="event-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Type</Label>
            <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
              <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                {["meeting", "deadline", "launch", "production", "payment", "followup", "milestone"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Location</Label>
            <Input value={form.location || ""} onChange={(e) => set("location", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Starts at</Label>
            <Input type="datetime-local" value={form.starts_at || ""} onChange={(e) => set("starts_at", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Ends at</Label>
            <Input type="datetime-local" value={form.ends_at || ""} onChange={(e) => set("ends_at", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Project</Label>
            <Select value={form.project_id || "__none"} onValueChange={(v) => set("project_id", v === "__none" ? "" : v)}>
              <SelectTrigger className="mt-1.5 bg-white/5 border-white/10"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent className="bg-[#151A23] border-white/10 text-white">
                <SelectItem value="__none">None</SelectItem>
                {projects?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/60 text-xs uppercase tracking-wider">Primary contact</Label>
            <div className="mt-1.5"><ContactPicker value={form.contact_id} onChange={(id) => set("contact_id", id)} entityId={entityId} /></div>
          </div>
          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Participants / attendees</Label>
            <div className="mt-1.5" data-testid="event-participants-picker">
              <MultiContactPicker values={form.participant_contact_ids} onChange={(ids) => set("participant_contact_ids", ids)} entityId={entityId} projectId={form.project_id} />
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-white/60 text-xs uppercase tracking-wider">Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1.5 bg-white/5 border-white/10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="event-save-btn" className="rounded-full bg-white text-black hover:bg-white/90">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
